<template>
  <div class="pwd-root">
    <!-- 单行：输入框 + 图标按钮组 -->
    <div class="pwd-row">
      <!-- 锁图标 -->
      <span class="pwd-lock" :class="{ 'is-focus': isFocused }">
        <i class="el-icon-lock"></i>
      </span>

      <!-- 原生输入框（不用 el-input，彻底避免插槽冲突） -->
      <input
          ref="inputEl"
          class="pwd-input"
          :class="{ 'is-error': false }"
          :type="visible ? 'text' : 'password'"
          :value="value"
          :placeholder="placeholder"
          :disabled="disabled"
          @input="onInput"
          @focus="isFocused = true"
          @blur="isFocused = false"
      />

      <!-- 右侧图标按钮组 -->
      <div class="pwd-icons">
        <!-- 显示/隐藏 -->
        <span
            class="pwd-icon-btn"
            :title="visible ? '隐藏密码' : '显示密码'"
            @click="toggleVisible"
        >
          <i :class="visible ? 'icon-eye-open' : 'icon-eye-closed'"></i>
        </span>

        <!-- 分隔线 -->
        <span class="pwd-sep" v-if="showRandom || showCopy"></span>

        <!-- 随机生成 -->
        <span
            v-if="showRandom"
            class="pwd-icon-btn pwd-icon-random"
            title="随机生成"
            @click="handleRandom"
        >
          <i class="el-icon-refresh-right" :class="{ 'is-spin': isSpinning }"></i>
        </span>

        <!-- 复制 -->
        <span
            v-if="showCopy && value"
            class="pwd-icon-btn pwd-icon-copy"
            :title="copied ? '已复制' : '复制密码'"
            @click="handleCopy"
        >
          <i :class="copied ? 'el-icon-check' : 'el-icon-document-copy'"></i>
        </span>
      </div>
    </div>

    <!-- 强度条 + 标签 -->
    <div v-if="showStrength && value" class="pwd-strength">
      <div class="pwd-strength-track">
        <div
            class="pwd-strength-fill"
            :style="{ width: strengthPercent + '%' }"
            :data-level="strengthLevel"
        ></div>
      </div>
      <span class="pwd-strength-text" :data-level="strengthLevel">
        {{ strengthLabel }}
      </span>
    </div>

    <!-- 规则提示 -->
    <div v-if="showTips && value" class="pwd-rules">
      <div
          v-for="rule in ruleList"
          :key="rule.key"
          class="pwd-rule-item"
          :class="{ 'is-valid': rule.valid }"
      >
        <i :class="rule.valid ? 'el-icon-success' : 'el-icon-error'"></i>
        <span>{{ rule.text }}</span>
      </div>
    </div>
  </div>
</template>

<script>
const DEFAULT_RULES = [
  { key: 'length',  text: '至少 8 位字符',           test: v => v.length >= 8 },
  { key: 'upper',   text: '包含大写字母 (A-Z)',      test: v => /[A-Z]/.test(v) },
  { key: 'lower',   text: '包含小写字母 (a-z)',      test: v => /[a-z]/.test(v) },
  { key: 'number',  text: '包含数字 (0-9)',          test: v => /[0-9]/.test(v) },
  { key: 'special', text: '包含特殊字符 (!@#$%^&*)', test: v => /[^A-Za-z0-9]/.test(v) }
]

const CHAR_POOLS = {
  upper:   'ABCDEFGHJKLMNPQRSTUVWXYZ',
  lower:   'abcdefghijkmnpqrstuvwxyz',
  number:  '23456789',
  special: '!@#$%^&*_+-='
}

export default {
  name: 'PasswordInput',

  model: {
    prop: 'value',
    event: 'input'
  },

  props: {
    value:        { type: String, default: '' },
    placeholder:  { type: String, default: '请输入密码' },
    disabled:     { type: Boolean, default: false },
    showStrength: { type: Boolean, default: true },
    showTips:     { type: Boolean, default: false },
    showRandom:   { type: Boolean, default: true },
    showCopy:     { type: Boolean, default: false },
    randomLength:  { type: Number, default: 12 },
    customStrength: { type: Function, default: null },
    customRules:    { type: Array, default: null }
  },

  data() {
    return {
      visible: false,
      isFocused: false,
      isSpinning: false,
      copied: false
    }
  },

  computed: {
    ruleList() {
      const rules = this.customRules || DEFAULT_RULES
      return rules.map(r => ({
        ...r,
        valid: r.test(this.value || '')
      }))
    },

    passedCount() {
      return this.ruleList.filter(r => r.valid).length
    },

    totalRules() {
      return this.ruleList.length
    },

    strengthLevel() {
      if (this.customStrength) return this.customStrength(this.value || '')
      const v = this.value || ''
      if (!v) return 0
      const passed = this.passedCount
      if (v.length >= 8 && passed <= 2) return 1
      if (passed === 3) return 2
      if (passed >= 4) return 3
      return 1
    },

    strengthPercent() {
      if (!this.value) return 0
      return Math.min(100, (this.passedCount / this.totalRules) * 100)
    },

    strengthLabel() {
      return { 0: '', 1: '弱', 2: '中', 3: '强' }[this.strengthLevel] || ''
    }
  },

  methods: {
    onInput(e) {
      const val = e.target.value
      this.$emit('input', val)
      this.$emit('change', val)
    },

    toggleVisible() {
      this.visible = !this.visible
      this.$nextTick(() => this.$refs.inputEl && this.$refs.inputEl.focus())
    },

    handleRandom() {
      const len = this.randomLength
      let result = [
        this._randChar(CHAR_POOLS.upper),
        this._randChar(CHAR_POOLS.lower),
        this._randChar(CHAR_POOLS.number),
        this._randChar(CHAR_POOLS.special)
      ]
      const all = CHAR_POOLS.upper + CHAR_POOLS.lower + CHAR_POOLS.number + CHAR_POOLS.special
      for (let i = 4; i < len; i++) result.push(this._randChar(all))
      for (let i = result.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[result[i], result[j]] = [result[j], result[i]]
      }
      const newPwd = result.join('')
      this.$emit('input', newPwd)
      this.$emit('random', newPwd)

      // 旋转动画
      this.isSpinning = true
      setTimeout(() => { this.isSpinning = false }, 500)

      this.$message && this.$message.success('已生成随机密码')
    },

    async handleCopy() {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(this.value)
        } else {
          const ta = document.createElement('textarea')
          ta.value = this.value
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
        }
        this.copied = true
        this.$message && this.$message.success('已复制')
        this.$emit('copy', this.value)
        setTimeout(() => { this.copied = false }, 1200)
      } catch (e) {
        this.$message && this.$message.error('复制失败')
      }
    },

    getStrengthLevel() { return this.strengthLevel },
    isValid(minLevel = 2) { return this.strengthLevel >= minLevel },

    _randChar(chars) { return chars.charAt(Math.floor(Math.random() * chars.length)) }
  }
}
</script>

<style scoped>
/* ============================================
   PasswordInput — 纯原生输入框版（无 el-input）
   彻底避免 el-input 插槽/scoped 穿透问题
   ============================================ */


/* 纯 CSS，不依赖任何图标字体 */
.icon-eye-open::after  { content: "👁️"; }
.icon-eye-closed::after { content: "🙈"; }
/* ========== 根容器 ========== */
.pwd-root {
  width: 100%;
  font-size: 14px;
  color: #303133;
}

/* ========== 第一行：锁 + 输入框 + 图标按钮 ========== */
.pwd-row {
  display: flex;
  align-items: center;
  width: 100%;
  height: 36px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #ffffff;
  transition: border-color .2s, box-shadow .2s;
  overflow: hidden;
}

/* 聚焦态 */
.pwd-row:focus-within {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, .15);
}

/* 禁用态 */
.pwd-row.is-disabled {
  background: #f5f7fa;
  cursor: not-allowed;
}

/* ========== 锁图标 ========== */
.pwd-lock {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 100%;
  flex-shrink: 0;
  color: #c0c4cc;
  font-size: 14px;
  border-right: 1px solid #ebeef5;
  transition: color .2s, background .2s;
}

.pwd-lock i {
  transition: transform .2s;
}

/* 聚焦时锁变蓝 + 微放大 */
.pwd-lock.is-focus {
  color: #409eff;
  background: #f0f7ff;
}

.pwd-lock.is-focus i {
  transform: scale(1.1);
}

/* ========== 原生输入框 ========== */
.pwd-input {
  flex: 1;
  height: 100%;
  border: none;
  outline: none;
  padding: 0 10px;
  font-size: 14px;
  color: #303133;
  background: transparent;
  min-width: 0; /* 防止 flex 子项溢出 */
}

.pwd-input::placeholder {
  color: #a8abb2;
}

.pwd-input:disabled {
  cursor: not-allowed;
  color: #a8abb2;
}

/* ========== 右侧图标按钮组 ========== */
.pwd-icons {
  display: flex;
  align-items: center;
  height: 100%;
  flex-shrink: 0;
  padding: 0 4px;
  gap: 2px;
}

/* 单个图标按钮 */
.pwd-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 3px;
  cursor: pointer;
  color: #909399;
  font-size: 14px;
  transition: color .15s, background .15s;
  user-select: none;
}

.pwd-icon-btn:hover {
  background: #f2f6fc;
  color: #409eff;
}

/* 随机图标 — 蓝色系 */
.pwd-icon-random:hover {
  color: #409eff;
  background: #ecf5ff;
}

/* 复制图标 — 绿色系 */
.pwd-icon-copy:hover {
  color: #67c23a;
  background: #f0f9eb;
}

/* 分隔线 */
.pwd-sep {
  display: inline-block;
  width: 1px;
  height: 16px;
  background: #dcdfe6;
  margin: 0 2px;
}

/* 旋转动画 */
.is-spin {
  animation: pwd-rotate .5s ease;
}

@keyframes pwd-rotate {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

/* ========== 强度条 ========== */
.pwd-strength {
  display: flex;
  align-items: center;
  margin-top: 6px;
  gap: 8px;
}

.pwd-strength-track {
  flex: 1;
  height: 4px;
  background: #f0f2f5;
  border-radius: 2px;
  overflow: hidden;
}

.pwd-strength-fill {
  height: 100%;
  border-radius: 2px;
  transition: width .35s ease, background .35s ease;
}

.pwd-strength-fill[data-level="1"] { background: #f56c6c; }
.pwd-strength-fill[data-level="2"] { background: #e6a23c; }
.pwd-strength-fill[data-level="3"] { background: #67c23a; }

.pwd-strength-text {
  font-size: 12px;
  font-weight: 600;
  min-width: 20px;
  text-align: right;
  line-height: 1;
}

.pwd-strength-text[data-level="1"] { color: #f56c6c; }
.pwd-strength-text[data-level="2"] { color: #e6a23c; }
.pwd-strength-text[data-level="3"] { color: #67c23a; }

/* ========== 规则提示 ========== */
.pwd-rules {
  margin-top: 6px;
  padding: 6px 10px;
  background: #fafbfc;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.9;
}

.pwd-rule-item {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #f56c6c;
}

.pwd-rule-item.is-valid {
  color: #67c23a;
}

.pwd-rule-item i {
  font-size: 12px;
  flex-shrink: 0;
}

/* ========== 暗色模式 ========== */
@media (prefers-color-scheme: dark) {
  .pwd-row {
    background: #1d1e1f;
    border-color: #4c4d4f;
  }
  .pwd-input {
    color: #e5eaf3;
  }
  .pwd-input::placeholder {
    color: #6b6d72;
  }
  .pwd-lock {
    border-right-color: #4c4d4f;
    color: #6b6d72;
  }
  .pwd-rules {
    background: #1d1e1f;
    border-color: #4c4d4f;
  }
  .pwd-strength-track {
    background: #363637;
  }
  .pwd-sep {
    background: #4c4d4f;
  }
}

</style>
